// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsDropdownOptions from './SolyticsDropdownOptions';
import SolyticsSearchBar from './SolyticsSearchBar';
import SolyticsDropdownInput from './SolyticsDropdownInput';

/**
 * SolyticsDropdownPanel - The dropdown content panel rendered inside SolyticsDropdownInput's popper
 *
 * @component
 * @description
 * SolyticsDropdownPanel renders the inner content of a dropdown: optional filter selector,
 * search bar, tab navigation, and the scrollable options list via SolyticsDropdownOptions.
 * Supports multi-select with "Select All", search filtering, tabbed grouping, and filter-dropdown variants.
 *
 * @param {string} id - HTML id prefix for child elements
 * @param {string} name - Name attribute passed to child filter dropdown
 * @param {string} variant - Panel variant: 'basic' | 'multi' | 'search' | 'tabs-single' | 'tabs-multi' | 'filter-dropdown' | 'icon'
 * @param {Array<{label: string, value: *}>} [filters=[]] - Filter options for 'filter-dropdown' variant
 * @param {string} [filterKey] - Object key used to match options against the active filter
 * @param {boolean} [searchable=false] - If true, shows a search bar to filter options by label
 * @param {Object} [tabs={}] - Tab-grouped options: { tabName: [...options] } (for tabs-single/tabs-multi)
 * @param {boolean} [showIcons=false] - If true, shows icons next to each option
 * @param {Array<{label: string, value: *}>} [options=[]] - Flat options list (used when not tabbed)
 * @param {function} onSelect - Selection handler; receives value (single) or array (multi/selectAll)
 * @param {Array} [selectedValues=[]] - Currently selected values for highlighting and checkbox state
 * @param {boolean} [withCheckbox=false] - If true, renders checkboxes next to options
 * @param {boolean} [multiple=false] - If true, enables multi-select with "Select All" option
 * @param {string} [noOptionsText='No options available.'] - Text shown when no options match
 *
 * @returns {React.ReactElement} A dropdown panel element
 */
const SolyticsDropdownPanel = ({
  id,
  name,
  variant,
  filters = [],
  filterKey,
  searchable,
  tabs = {},
  showIcons,
  options = [],
  onSelect,
  selectedValues = [],
  withCheckbox,
  multiple,
  noOptionsText = 'No options available.',
}) => {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(Object.keys(tabs || {})[0] || '');

  let filteredOptions = [...(options || [])];

  if (variant === 'filter-dropdown' && filterKey) {
    if (activeFilter !== 'all') {
      filteredOptions = filteredOptions?.filter(
        (opt) => opt?.[filterKey] === activeFilter
      );
    }
  }

  if (variant?.includes('tabs') && tabs) {
    filteredOptions = tabs?.[activeTab] || [];
  }

  // Add Select All for multi-select
  if (multiple) {
    filteredOptions = [
      { label: '(Select All)', value: 'selectAll' },
      ...filteredOptions,
    ];
  }

  // Real options (excluding selectAll)
  const realOptions = filteredOptions?.filter((o) => o?.value !== 'selectAll');

  const isSelectAllChecked =
    realOptions?.length &&
    realOptions?.every((o) => selectedValues?.includes(o?.value));

  const isSelectAllIndeterminate =
    realOptions?.some((o) => selectedValues?.includes(o?.value)) &&
    !isSelectAllChecked;

  const handleOptionSelect = (val) => {
    if (val === 'selectAll') {
      const allValues = realOptions?.map((o) => o?.value);

      const updated = isSelectAllChecked
        ? selectedValues?.filter((v) => !allValues?.includes(v))
        : [...new Set([...(selectedValues || []), ...allValues])];

      onSelect?.(updated);
    } else {
      onSelect?.(val);
    }
  };

  if (searchable && search?.trim()) {
    filteredOptions = filteredOptions?.filter((o) =>
      o?.label?.toLowerCase().includes(search?.toLowerCase())
    );
  }

  return (
    <SolyticsBox sx={{ py: 1 }}>
      {/* FILTER SELECTOR */}
      {variant === 'filter-dropdown' && (
        <SolyticsBox sx={{ px: 1, mb: 1 }}>
          <SolyticsDropdownInput
            id={`children-${id}`}
            name={name}
            size="small"
            value={activeFilter}
            onChange={(e) => setActiveFilter(e?.target?.value)}
            options={filters}
          />
        </SolyticsBox>
      )}

      {/* SEARCH FIELD */}
      {(searchable || variant?.includes('tabs')) && (
        <SolyticsBox sx={{ px: 1, mb: 1 }}>
          <SolyticsSearchBar
            name="dropdown-search"
            fullWidth
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
            sx={{ mb: 1 }}
          />
        </SolyticsBox>
      )}

      {/* TABS */}
      {variant?.includes('tabs') && (
        <SolyticsBox
          sx={{ display: 'flex', px: 1, py: 1.5, gap: 2 }}
          role="tablist"
        >
          {Object.keys(tabs)?.map((t) => {
            const isActive = activeTab === t;
            return (
              <SolyticsBox
                key={t}
                role="tab"
                tabIndex={isActive ? 0 : -1}
                aria-selected={isActive}
                onClick={() => setActiveTab(t)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveTab(t);
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 400,
                  color: (theme) =>
                    isActive
                      ? theme.palette.grey.T60
                      : theme.palette.text.disabled,
                  '&:focus-visible': {
                    outline: (theme) =>
                      `2px solid ${theme.palette.lavenderBlue[80]}`,
                    outlineOffset: '2px',
                    borderRadius: '4px',
                  },
                }}
              >
                {t}
              </SolyticsBox>
            );
          })}
        </SolyticsBox>
      )}

      {/* OPTIONS */}
      <SolyticsBox sx={{ maxHeight: 264, overflowY: 'auto' }}>
        {filteredOptions?.length ? (
          filteredOptions?.map((opt) => (
            <SolyticsDropdownOptions
              key={opt?.value}
              option={opt}
              selected={
                opt?.value === 'selectAll'
                  ? isSelectAllChecked
                  : selectedValues?.includes(opt?.value)
              }
              indeterminate={
                opt?.value === 'selectAll' ? isSelectAllIndeterminate : false
              }
              showCheckbox={withCheckbox}
              showIcons={showIcons}
              onSelect={() => handleOptionSelect(opt?.value)}
            />
          ))
        ) : (
          <SolyticsBox sx={{ py: 6, textAlign: 'center' }}>
            <SolyticsTypography color={(theme) => theme.palette.text.disabled}>
              {noOptionsText}
            </SolyticsTypography>
          </SolyticsBox>
        )}
      </SolyticsBox>
    </SolyticsBox>
  );
};

export default SolyticsDropdownPanel;

/**
 * ## Features
 * - Filter selector dropdown for 'filter-dropdown' variant (filters options by a key)
 * - Search bar for 'search', 'tabs-single', and 'tabs-multi' variants
 * - Tab navigation for tabbed variants with keyboard support (Enter/Space)
 * - "(Select All)" option auto-injected in multi-select mode
 * - Select All supports checked and indeterminate states
 * - Scrollable options list (max 264px height)
 * - Empty state with configurable noOptionsText
 * - Delegates option rendering to SolyticsDropdownOptions
 *
 * ## Examples
 *
 * ```jsx
 * // Basic options panel
 * <SolyticsDropdownPanel
 *   id="status"
 *   name="status"
 *   variant="basic"
 *   options={[{ label: 'Active', value: 'active' }]}
 *   selectedValues={['active']}
 *   onSelect={handleSelect}
 * />
 *
 * // Tabbed multi-select with search
 * <SolyticsDropdownPanel
 *   id="categories"
 *   name="categories"
 *   variant="tabs-multi"
 *   tabs={{ Fruits: fruitOptions, Veggies: veggieOptions }}
 *   multiple
 *   withCheckbox
 *   searchable
 *   selectedValues={selected}
 *   onSelect={handleSelect}
 * />
 * ```
 */
